'use client'

// The connected wallet's own Twetch profile — who "you" are, for the
// composer's face, the editor and anything else that addresses the viewer.
//
// Known from the wallet alone: Twetch's public address index names the
// account, so this works with no Twetch session (a fresh browser). The full
// profile shares its SWR key with the profile page, so an edit made anywhere
// shows everywhere at once.

import useSwr from 'swr'
import Wallet from '@/utils/wallet'
import useLoggedIn from '@/utils/hooks/useLoggedIn'
import { twetchJson } from './twetch-api'
import useTwetchIdentities from './useTwetchIdentities'

export interface ViewerProfile {
	id: number
	name?: string | null
	icon?: string | null
	banner?: string | null
	description?: string | null
	verifiedIcon?: string | null
	numFollowers?: number
	numFollowing?: number
}

export function viewerProfileKey(userId: number): string {
	return `alkanes-twetch-profile-${userId}`
}

export default function useViewerProfile(): {
	address: string | null
	/// Null while unknown or when this wallet has no Twetch account yet.
	userId: number | null
	name: string | null
	icon: string | null
	profile: ViewerProfile | undefined
} {
	const loggedIn = useLoggedIn()
	const address = loggedIn ? Wallet.getTaprootAddress() || null : null
	const identities = useTwetchIdentities([address])
	const base = address ? identities[address] : undefined
	const { data: profile } = useSwr<ViewerProfile>(
		base ? viewerProfileKey(base.id) : null,
		() => twetchJson<ViewerProfile>(`/v1/users/${base!.id}`),
		{ revalidateOnFocus: false }
	)
	return {
		address,
		userId: base?.id ?? null,
		name: profile?.name ?? base?.name ?? null,
		icon: profile?.icon ?? base?.icon ?? null,
		profile
	}
}
